import classnames from 'classnames'
import {Link, matchPath, useLocation} from 'react-router-dom'

import {TooltipBody, TooltipWrapper} from 'components'

export type SegmentedToggleItem = {
  id: string
  link: string
  text: string
  isDisabled?: boolean
  disabledTooltip?: string
}

export type SegmentedToggleProps = {
  segments: SegmentedToggleItem[]
  wrapperClassName?: string
  dataCy?: string
  tooltipWrapperId?: string
}

export const SegmentedToggle = ({
  segments,
  wrapperClassName = '',
  dataCy = 'segmented-toggle',
  tooltipWrapperId,
}: SegmentedToggleProps) => {
  const {pathname} = useLocation()

  const baseSegmentClasses =
    'abc-regular text-sm relative w-full shrink-0 inline-flex items-center justify-center rounded-0.75 px-3 py-1 transition-all duration-150'

  return (
    <div
      data-cy={dataCy}
      className={classnames(
        'flex p-0.75 items-center gap-0.75 self-stretch rounded-0.75 bg-white/5',
        wrapperClassName,
      )}
    >
      {segments.map(segment => {
        const isActive = Boolean(
          matchPath(pathname, {
            path: segment.link,
            exact: true,
          }),
        )
        const isDisabled = !!segment.isDisabled

        const disabledLinkProps = isDisabled
          ? {
              tabIndex: -1,
              'aria-disabled': true,
              'data-tooltip-id': 'segmented-toggle-tooltip',
              'data-tooltip-content': segment.disabledTooltip,
            }
          : {}

        return (
          <div key={segment.id} className="flex-1 min-w-0" data-cy={segment.id}>
            {isDisabled ? (
              <span
                className={classnames(
                  baseSegmentClasses,
                  'text-white/40 cursor-not-allowed',
                )}
                aria-label={segment.text}
                {...disabledLinkProps}
              >
                {segment.text}
              </span>
            ) : (
              <Link
                to={segment.link}
                className={classnames(baseSegmentClasses, {
                  'z-10 bg-white/20 text-white cursor-default': isActive,
                  'text-gray-1100 cursor-pointer hover:text-white hover:bg-white/5':
                    !isActive,
                })}
                aria-current={isActive ? 'page' : undefined}
                aria-label={segment.text}
              >
                {segment.text}
              </Link>
            )}
          </div>
        )
      })}

      {/* Tooltip for the segmented toggle when a segment is disabled */}
      <TooltipWrapper
        id={tooltipWrapperId}
        place="bottom"
        className="z-100"
        render={({content}) => <TooltipBody noPadding>{content}</TooltipBody>}
      />
    </div>
  )
}
